import React from 'react';

interface StatusStep {
  key: string;
  label: string;
  completed: boolean;
  current: boolean;
}

interface StatusTimelineProps {
  currentStatus: string;
  className?: string;
}

const StatusTimeline: React.FC<StatusTimelineProps> = ({
  currentStatus,
  className = ''
}) => {
  // Define the status flow
  const statusSteps: StatusStep[] = [
    { key: 'pending', label: 'Pending', completed: false, current: false },
    { key: 'process', label: 'In Process', completed: false, current: false },
    { key: 'completed', label: 'Completed', completed: false, current: false },
    { key: 'invoiced', label: 'Invoiced', completed: false, current: false },
  ];

  // Determine status progression
  const statusOrder = ['pending', 'process', 'completed', 'invoiced'];
  const currentIndex = statusOrder.indexOf(currentStatus);

  // Update status steps based on current status
  statusSteps.forEach((step, index) => {
    if (index < currentIndex) {
      step.completed = true;
      step.current = false;
    } else if (index === currentIndex) {
      step.completed = false;
      step.current = true;
    } else {
      step.completed = false;
      step.current = false;
    }
  });

  return (
    <div className={`bg-white shadow rounded-lg overflow-hidden ${className}`}>
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <h3 className="text-lg leading-6 font-medium text-gray-900">Status Timeline</h3>
      </div>
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between">
          {statusSteps.map((step, index) => (
            <React.Fragment key={step.key}>
              <div className="flex flex-col items-center">
                {/* Status Circle */}
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                    step.completed
                      ? 'bg-green-500 text-white'
                      : step.current
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {step.completed ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  ) : step.current ? (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <div className="w-3 h-3 bg-gray-300 rounded-full"></div>
                  )}
                </div>

                {/* Status Label */}
                <div className="mt-2 text-center">
                  <div
                    className={`text-xs font-medium ${
                      step.completed
                        ? 'text-green-600'
                        : step.current
                        ? 'text-blue-600'
                        : 'text-gray-500'
                    }`}
                  >
                    {step.label}
                  </div>
                </div>
              </div>

              {/* Connector Line */}
              {index < statusSteps.length - 1 && (
                <div
                  className={`flex-1 h-0.5 mx-2 ${
                    step.completed ? 'bg-green-500' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StatusTimeline;